/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { Grid, makeStyles, Checkbox, Typography, Box } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import DynamicFilter from 'components/DynamicFilter';
import { useTableFilters } from 'hooks/useTableFilters';
import {
  UsersForRecoveryListProps,
  UserForRecoveryFields,
} from 'modules/users/interfaces';
import {
  getUsersForRecoveryRequest,
  recoverUsersByRecoveryUuidsRequest,
  recoverUsersByRequestUuidRequest,
  removeUserPermanentlyRequest,
  removeMultipleUsersPermanentlyRequest,
} from 'modules/users/actions';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';
import { formatDateValue } from 'utils/dateFormat';
import { setFilterValues } from 'modules/filters/actions';
import { FilterFields } from 'modules/filters/interfaces';
import PageTitle from 'components/PageTitle';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import Button from 'components/Button';
import palette from 'theme/palette';
import { history } from 'store';
import { useCheckboxes } from 'hooks';
import { RolesEnum, PermissionsData } from 'modules/authenticate/interfaces';
import Permission from 'components/ValidPermission';
import ButtonGroup from 'components/ButtonGroup';
import styles from './styles';
import { headers } from './tableHeaders';
import { filterOptions } from './filterOptions';
import RecoverUserByRequestUuidModalContent from './RecoverUserByRequestUuidModalContent';

const useStyles = makeStyles(styles);

interface ListUsersForRecoveryProps {
  filters: FilterFields[];
  isLoading: boolean;
  permissions?: PermissionsData;
  role?: RolesEnum;
  usersForRecoveryList: UsersForRecoveryListProps;
  getUsersForRecoveryRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  recoverUsersByRecoveryUuidsRequest: ({ recoveryUuids, query }) => void;
  recoverUsersByRequestUuidRequest: ({ requestUuid, query }) => void;
  removeMultipleUsersPermanentlyRequest: ({
    usersToRemove,
    getUsersFn,
  }) => void;
  removeUserPermanentlyRequest: ({ userUuid, getUsersFn }) => void;
  setFilterValues: (props: FilterFields[]) => void;
}

const MAX_COUNT_FOR_SELECT = 500;

const ListUsersForRecovery = ({
  filters,
  isLoading,
  permissions,
  role,
  usersForRecoveryList,
  getUsersForRecoveryRequest,
  modalClose,
  modalOpen,
  recoverUsersByRecoveryUuidsRequest,
  recoverUsersByRequestUuidRequest,
  removeMultipleUsersPermanentlyRequest,
  removeUserPermanentlyRequest,
  setFilterValues,
}: ListUsersForRecoveryProps) => {
  const classes = useStyles();
  const {
    itemOrder,
    searchValue,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters();
  const {
    checks,
    handleCheck,
    handleAllCheck,
    isChecked,
    clearChecks,
  } = useCheckboxes<
    UserForRecoveryFields,
    { recovery_uuid: string; user_uuid: string }
  >({
    items: usersForRecoveryList?.results,
    isItemChecked: (item, check) => item.recovery_uuid === check.recovery_uuid,
    compareChecks: (item, check) => item.recovery_uuid === check.recovery_uuid,
    createCheckItem: item => ({
      recovery_uuid: item.recovery_uuid,
      user_uuid: item.user_uuid,
    }),
  });

  const [listSwitch, setListSwitch] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const exceededSelectedLimit = checks.length > MAX_COUNT_FOR_SELECT;
  const isAllChecked =
    !!usersForRecoveryList?.results?.length &&
    usersForRecoveryList.results.every(item =>
      isChecked({
        recovery_uuid: item.recovery_uuid,
        user_uuid: item.user_uuid,
      })
    );

  const filterValues = React.useMemo(() => {
    return filters?.reduce(
      (acc, { key, value }) => ({ ...acc, [key]: value || '' }),
      {} as { [key: string]: string }
    );
  }, [filters]);

  React.useEffect(() => {
    setQuery(prev => ({ ...prev, ...filterValues }));
  }, [filterValues, setQuery]);

  const getUsersForRecovery = (refresh = false) => {
    getUsersForRecoveryRequest({
      ...query,
    });

    setRefresh(refresh);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (
        usersForRecoveryList.results.some(
          user => user.recovery_in_progress || user.is_removing_permanently
        )
      ) {
        getUsersForRecovery(true);
      } else {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersForRecoveryList]);

  React.useEffect(() => {
    setFilterValues(filterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  React.useEffect(() => {
    if (listSwitch === true) {
      getUsersForRecovery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleRecoverUserByRecoveryUuid = (recoveryUuids: string[]) => {
    const title =
      recoveryUuids.length > 1
        ? i18n.t('USERS_PAGE.RECOVER_MULTIPLE_USERS_MODAL_TITLE_KEY')
        : i18n.t('USERS_PAGE.RECOVER_USER_MODAL_TITLE_KEY');
    const content =
      recoveryUuids.length > 1
        ? i18n.t('USERS_PAGE.RECOVER_MULTIPLE_USERS_MODAL_CONTENT_KEY', {
            count: recoveryUuids.length,
          })
        : i18n.t('USERS_PAGE.RECOVER_USER_MODAL_CONTENT_KEY');

    modalOpen({
      title,
      content,
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              recoverUsersByRecoveryUuidsRequest({
                recoveryUuids: Array.from(recoveryUuids),
                query,
              });
              modalClose();
              clearChecks();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRecoverUserByRequestUuid = (requestUuid: string) => {
    modalOpen({
      title: i18n.t('USERS_PAGE.RECOVER_USER_BY_REQUEST_MODAL_TITLE_KEY'),
      content: (
        <RecoverUserByRequestUuidModalContent requestUuid={requestUuid} />
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              recoverUsersByRequestUuidRequest({ requestUuid, query });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRemoveUserPermanently = (userUuid: string) => {
    modalOpen({
      title: i18n.t('USERS_PAGE.REMOVE_USER_PERMANENTLY_MODAL_TITLE_KEY'),
      content: i18n.t('USERS_PAGE.REMOVE_USER_PERMANENTLY_DESCRIPTION_KEY'),
      footer: (
        <Grid>
          <Button
            deleteButton
            id="delete-button"
            onClick={() => {
              removeUserPermanentlyRequest({
                userUuid,
                getUsersFn: () => getUsersForRecovery(),
              });
              modalClose();
            }}
          >
            {i18n.t('DELETE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRemoveMultipleUsersPermanently = () => {
    const usersToRemove = new Set(checks.map(item => item.user_uuid));

    modalOpen({
      title: i18n.t('USERS_PAGE.REMOVE_USER_PERMANENTLY_MODAL_TITLE_KEY'),
      content: i18n.t(
        'USERS_PAGE.REMOVE_MULTIPLE_USERS_PERMANENTLY_DESCRIPTION_KEY',
        { counter: usersToRemove.size }
      ),
      footer: (
        <Grid>
          <Button
            deleteButton
            id="delete-button"
            onClick={() => {
              removeMultipleUsersPermanentlyRequest({
                usersToRemove,
                getUsersFn: () => getUsersForRecovery(),
              });
              modalClose();
              clearChecks();
            }}
          >
            {i18n.t('DELETE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const getStatusTooltip = (item: UserForRecoveryFields): string => {
    if (item?.recovery_in_progress) return i18n.t('RECOVERING_KEY');
    if (item?.is_removing_permanently) return i18n.t('REMOVING_KEY');
    if (item?.active) return i18n.t('ACTIVE_KEY');

    return i18n.t('INACTIVE_KEY');
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('USERS_PAGE.DELETED_USERS_TITLE_KEY')}
          subtitle={i18n.t('USERS_PAGE.DELETED_USERS_SUBTITLE_KEY')}
        />

        <Grid item xs={4} className={classes.actionsButtonsGrid}>
          <Button
            startIcon={<ChevronLeftIcon className={classes.backIcon} />}
            variant="outlined"
            id="back-button"
            onClick={() =>
              history.push(
                role === RolesEnum.superAdmin
                  ? '/all-users/management'
                  : '/all-users'
              )
            }
          >
            {i18n.t('BACK_KEY')}
          </Button>
          <ButtonGroup
            label={i18n.t('GENERAL_ACTIONS_KEY')}
            options={[
              {
                label: i18n.t('USERS_PAGE.RECOVER_MULTIPLE_USERS_ACTION_KEY'),
                onClick: () =>
                  handleRecoverUserByRecoveryUuid(
                    checks.map(item => item.recovery_uuid)
                  ),
                disabled: exceededSelectedLimit || !checks.length,
                hidden: !permissions?.users_recovery,
              },
              {
                label: i18n.t(
                  'USERS_PAGE.DELETE_MULTIPLE_USERS_PERMANENTLY_ACTION_KEY'
                ),
                onClick: () => handleRemoveMultipleUsersPermanently(),
                disabled: exceededSelectedLimit || !checks.length,
                hidden: !permissions?.delete_user_permanently,
              },
            ]}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.searchContainer}>
        <DynamicFilter
          isLoading={isLoading}
          listSwitch={listSwitch}
          setListSwitch={setListSwitch}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
        />

        {listSwitch && (
          <>
            <Grid item className={classes.selectedRecoveriesCounter}>
              <Typography>
                {`${i18n.t('USERS_PAGE.SELECTED_REGISTERS_KEY')}: ${
                  checks.length
                } / `}
                <Box
                  color={exceededSelectedLimit ? palette.red : palette.green}
                  fontSize={12}
                  fontWeight="bold"
                  display="inline"
                >
                  {MAX_COUNT_FOR_SELECT}
                </Box>
              </Typography>
            </Grid>

            <TableItems
              header={
                <>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      id="select-all-users"
                      checked={isAllChecked}
                      onClick={() => handleAllCheck(isAllChecked)}
                    />
                  </TableCell>
                  {headers.map(item => (
                    <TableCell
                      centerTitle={item?.centerTitle}
                      key={item?.key}
                      order={item?.order}
                      markOderItem={itemOrder === item?.key}
                      handleOrder={() =>
                        item?.order ? handleOrder(item?.key) : null
                      }
                    >
                      {i18n.t(item?.i18n)}
                    </TableCell>
                  ))}
                </>
              }
              renderItems={(item: UserForRecoveryFields) => (
                <TableRow key={item.recovery_uuid}>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      id="select-user"
                      onClick={() =>
                        handleCheck({
                          recovery_uuid: item.recovery_uuid,
                          user_uuid: item.user_uuid,
                        })
                      }
                      checked={isChecked({
                        recovery_uuid: item.recovery_uuid,
                        user_uuid: item.user_uuid,
                      })}
                    />
                  </TableCell>
                  <TableCell
                    className={classes.centerFields}
                    tooltip={`${item?.first_name} ${item?.last_name}`}
                  >{`${item?.first_name} ${item?.last_name}`}</TableCell>
                  <TableCell
                    className={classes.emailField}
                    ellipsis
                    tooltip={item?.email}
                  >
                    {item?.email}
                  </TableCell>
                  <TableCell
                    className={classes.centerFields}
                    ellipsis
                    tooltip={item?.client?.name}
                  >
                    {item?.client?.name}
                  </TableCell>
                  <TableCell
                    className={classes.centerLongFields}
                    ellipsis
                    tooltip={item?.environment?.name}
                  >
                    {item?.environment?.name}
                  </TableCell>
                  <TableCell
                    className={classes.centerLongFields}
                    ellipsis
                    tooltip={item?.partner?.name}
                  >
                    {item?.partner?.name}
                  </TableCell>
                  <TableCell
                    className={classes.centerLongFields}
                    ellipsis
                    tooltip={item?.request_uuid}
                    copyable
                  >
                    {item?.request_uuid}
                  </TableCell>
                  <TableCell
                    className={classes.centerSmallFields}
                    tooltip={getStatusTooltip(item)}
                  >
                    <Grid container justify="center">
                      {item?.recovery_in_progress ||
                      item?.is_removing_permanently ? (
                        <StatusIcon status="loading" />
                      ) : (
                        <StatusIcon
                          status={item?.active ? 'success' : 'inactive'}
                        />
                      )}
                    </Grid>
                  </TableCell>
                  <TableCell centerTitle className={classes.centerFields}>
                    {formatDateValue(item?.latest_access)}
                  </TableCell>
                  <TableCell textAlignCenter>
                    <MenuActions id={`btn-options-${item?.recovery_uuid}`}>
                      <MenuActionItem
                        id="btn-recover-user"
                        onClick={() =>
                          handleRecoverUserByRecoveryUuid([item?.recovery_uuid])
                        }
                      >
                        {i18n.t('USERS_PAGE.RECOVER_USER_ACTION_KEY')}
                      </MenuActionItem>
                      <MenuActionItem
                        id="btn-recover-request"
                        onClick={() =>
                          handleRecoverUserByRequestUuid(item?.request_uuid)
                        }
                      >
                        {i18n.t(
                          'USERS_PAGE.RECOVER_USER_BY_REQUEST_ACTION_KEY'
                        )}
                      </MenuActionItem>
                      <Permission value="delete_user_permanently">
                        <MenuActionItem
                          id="btn-remove-permanently"
                          onClick={() =>
                            handleRemoveUserPermanently(item?.user_uuid)
                          }
                        >
                          {i18n.t('USERS_PAGE.REMOVE_USER_PERMANENTLY_KEY')}
                        </MenuActionItem>
                      </Permission>
                    </MenuActions>
                  </TableCell>
                </TableRow>
              )}
              data={usersForRecoveryList}
              isLoading={!refresh && isLoading}
              onChangePage={handlePageChange}
              onChangeLimit={handleLimitChange}
              notFoundText={i18n.t('USERS_PAGE.NO_USERS_KEY')}
            />
          </>
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  users: { isLoadingUsersForRecoveryList, isLoading, usersForRecoveryList },
  filters: { filters },
}: ApplicationState) => ({
  filters,
  isLoading: isLoadingUsersForRecoveryList || isLoading,
  permissions: data?.permissions,
  role: data?.role,
  usersForRecoveryList,
});
export default connect(mapStateToProps, {
  getUsersForRecoveryRequest,
  modalClose,
  modalOpen,
  recoverUsersByRecoveryUuidsRequest,
  recoverUsersByRequestUuidRequest,
  removeMultipleUsersPermanentlyRequest,
  removeUserPermanentlyRequest,
  setFilterValues,
})(ListUsersForRecovery);
